import { motion } from "framer-motion";
import {
  Phone,
  MapPin,
  AlertTriangle,
  Calendar,
  CircleCheckBig,
  Snowflake,
  Layers,
  ChevronRight,
} from "lucide-react";

const BG =
  "https://static.prod-images.emergentagent.com/jobs/51ddf5f6-e80c-4259-9ef6-c5393e7b5903/images/d4f1e4eab0ebe102b290e0d034845b0ff38508133a3e424b175f41c5f77fb1cc.png";

function Row({ icon: Icon, label, value, accent }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-mist-line py-3 last:border-b-0">
      <div className="flex items-center gap-2.5">
        <span className={`inline-flex h-7 w-7 items-center justify-center rounded-md ${accent ?? "bg-mist text-navy/70"}`}>
          <Icon className="h-3.5 w-3.5" />
        </span>
        <span className="text-[13px] font-medium text-navy/65">{label}</span>
      </div>
      <span className="text-right text-[13px] font-semibold text-navy">
        {value}
      </span>
    </div>
  );
}

export default function LeadSummary() {
  return (
    <section
      data-testid="lead-summary-section"
      aria-labelledby="lead-summary-heading"
      className="relative overflow-hidden bg-navy py-20 text-white sm:py-24"
    >
      {/* soft bg illustration */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.08]"
        style={{
          backgroundImage: `url(${BG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        aria-hidden="true"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-navy/85 via-navy/95 to-navy" aria-hidden="true" />

      <div className="container-page relative grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-14">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.55, ease: "easeOut" }}
          className="lg:col-span-5"
        >
          <span className="pill-dark">
            <Layers className="h-3 w-3 text-sun" />
            Contractor view
          </span>
          <h2
            id="lead-summary-heading"
            className="mt-4 font-display text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl lg:text-[2.5rem]"
          >
            Here&rsquo;s what the contractor sees.
          </h2>
          <p className="mt-4 max-w-xl text-base text-white/75 sm:text-lg">
            Every demo submission becomes a clean lead card &mdash; ready for
            the team or your CRM. Dispatcher gets context, technician walks in
            prepared.
          </p>
          <ul className="mt-6 space-y-2 text-sm text-white/70">
            <li className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-aqua" />
              Pipeline-ready statuses (New &rarr; Qualifying &rarr; Scheduled)
            </li>
            <li className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-aqua" />
              Priority tagging based on urgency + cooling status
            </li>
            <li className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-aqua" />
              Export-ready for HubSpot, ServiceTitan, or Google Sheets
            </li>
          </ul>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="lg:col-span-7"
        >
          <div
            data-testid="demo-lead-preview-card"
            className="relative mx-auto w-full max-w-xl rounded-3xl border border-white/10 bg-white p-1.5 shadow-lift"
          >
            <div className="rounded-[1.25rem] bg-white p-6 sm:p-7">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <span
                    data-testid="demo-lead-preview-badge"
                    className="inline-flex items-center gap-1.5 rounded-full border border-sun/40 bg-sun/15 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-sun"
                  >
                    <span className="h-1.5 w-1.5 rounded-full bg-sun" />
                    Demo Lead Preview
                  </span>
                  <h3 className="mt-3 font-display text-2xl font-bold text-navy">
                    Maria H.
                  </h3>
                  <p className="text-sm text-navy/60">Submitted just now · Web intake</p>
                </div>
                <div className="rounded-xl border border-mist-line bg-mist px-3 py-2 text-right">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-navy/55">
                    Priority
                  </p>
                  <p className="mt-0.5 inline-flex items-center gap-1 text-sm font-bold text-red-600">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    High
                  </p>
                </div>
              </div>

              <div className="mt-5">
                <Row icon={Phone} label="Phone" value="(555) 000-0142 · demo" />
                <Row icon={MapPin} label="City" value="Cape Coral, FL" />
                <Row icon={AlertTriangle} label="Issue" value="AC not cooling" accent="bg-red-50 text-red-600" />
                <Row icon={Calendar} label="Urgency" value="Today if available" accent="bg-sun/10 text-sun" />
                <Row icon={Layers} label="System age" value="10–15 years" />
                <Row icon={Snowflake} label="Cooling status" value="No · warm air only" accent="bg-aqua/10 text-aqua-deep" />
                <Row icon={CircleCheckBig} label="Recommended next step" value="High-priority callback" accent="bg-aqua/10 text-aqua-deep" />
              </div>

              {/* pipeline status bar */}
              <div className="mt-6">
                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-navy/55">
                  Pipeline status
                </p>
                <div className="mt-2 flex items-center gap-1.5 text-[11px] font-semibold">
                  <span className="rounded-full bg-navy px-2.5 py-1 text-white">New Lead</span>
                  <ChevronRight className="h-3 w-3 text-navy/40" />
                  <span className="rounded-full bg-aqua/15 px-2.5 py-1 text-aqua-deep">Qualifying</span>
                  <ChevronRight className="h-3 w-3 text-navy/30" />
                  <span className="rounded-full border border-mist-line px-2.5 py-1 text-navy/45">
                    Scheduled
                  </span>
                </div>
              </div>

              <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-mist-line pt-5">
                <p className="text-[11px] text-navy/55">
                  Demo data only · No real customer info shown on this site.
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="rounded-full border border-mist-line px-3 py-1.5 text-xs font-semibold text-navy/75 hover:bg-mist"
                  >
                    Snooze
                  </button>
                  <button
                    type="button"
                    className="rounded-full bg-navy px-3 py-1.5 text-xs font-semibold text-white hover:bg-navy-soft"
                  >
                    Assign technician
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
